import {
  MotionStyle,
  MotionValue,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  ArrowUpRight,
  Briefcase,
  Download,
  Github,
  Linkedin,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import resumeDocx from "../assets/Mehmet_Kaan_Taspunar_CV.docx";
import resumePreview from "../assets/Resume.png";
import advilia0 from "../assets/images/projects/advilia/advilia0.png";
import advilia1 from "../assets/images/projects/advilia/advilia1.png";
import advilia2 from "../assets/images/projects/advilia/advilia2.png";
import askGandalf0 from "../assets/images/projects/askgandalf/askgandalf0.png";
import cohabit0 from "../assets/images/projects/cohabit/cohabit0.png";
import cohabit1 from "../assets/images/projects/cohabit/cohabit1.png";
import cohabit2 from "../assets/images/projects/cohabit/cohabit2.png";
import educationGavle from "../assets/images/education/hgevle.png";
import educationKristianstad from "../assets/images/education/hkr.png";
import educationMalmo from "../assets/images/education/mau.png";
import educationUmea from "../assets/images/education/umeo.png";
import hediyapp0 from "../assets/images/projects/hediyapp/hediyapp0.png";
import heroSideCv from "../assets/images/cvSide.jpg";
import logbotai0 from "../assets/images/projects/logbotai/logbotai0.png";
import logbotai1 from "../assets/images/projects/logbotai/logbotai1.png";
import logbotai2 from "../assets/images/projects/logbotai/logbotai2.png";
import mrHangman0 from "../assets/images/projects/mrhangman/mrhangman0.png";
import promptStudio0 from "../assets/images/projects/promptstudio/promptstudio0.png";
import promptStudio1 from "../assets/images/projects/promptstudio/promptstudio1.png";
import promptStudio2 from "../assets/images/projects/promptstudio/promptstudio2.png";
import skillCloud from "../assets/images/skills/cloudservices.png";
import skillFrontend from "../assets/images/skills/frontend.png";
import zschatbot0 from "../assets/images/projects/zschatbot/zschatbot0.png";
import zschatbot1 from "../assets/images/projects/zschatbot/zschatbot1.png";
import zschatbot2 from "../assets/images/projects/zschatbot/zschatbot2.png";
import { Project } from "../data/projects";
import { useProjects } from "../hooks/useProjects";
import "../stylesheets/creatorPortfolio.css";

const contact = {
  email: "taspunar10@hotmail.com",
  phoneDisplay: "072 317 10 61",
  phoneHref: "tel:0046723171061",
  website: "https://mehmetkaantaspunar.se",
  location: "Malmö, Sweden",
  linkedin: "https://www.linkedin.com/in/mehmet-kaan-taspunar/",
  github: "https://github.com/Mehmet-Kaan",
  instagram: "https://www.instagram.com/kaan_tspnr/",
  facebook: "https://www.facebook.com/kaan.tspnr/",
};

const marqueeImages = [
  logbotai0,
  logbotai1,
  zschatbot0,
  zschatbot1,
  promptStudio0,
  promptStudio1,
  advilia0,
  advilia1,
  cohabit0,
  cohabit1,
  askGandalf0,
  hediyapp0,
  mrHangman0,
  logbotai2,
  zschatbot2,
  promptStudio2,
  advilia2,
  cohabit2,
];

const skills = [
  {
    description:
      "JavaScript, React, React Native, UI/UX design, Adobe Figma, Adobe XD, object-oriented Java foundations, and C# basics.",
    name: "Front-end Development",
  },
  {
    description:
      "Node.js, PHP, Firebase, API testing with Insomnia, backend workflows, and email automation for production business systems.",
    name: "Back-end Development",
  },
  {
    description:
      "Google Cloud Platform, Firestore, Pub/Sub, BigQuery, deployed cloud apps, and practical infrastructure for AI products.",
    name: "Cloud Services",
  },
  {
    description:
      "OpenAI API integrations, document-based Q&A, context-aware business chatbots, and AI assistants grounded in uploaded files.",
    name: "AI Products",
  },
  {
    description:
      "Blender 3D modeling, Android game development, Max/MSP sound processing, Phaser game work, Git, and GitHub.",
    name: "Creative Technology",
  },
];

const education = [
  {
    logo: educationMalmo,
    meta: "2024 - 2025, ongoing",
    school: "Malmö University",
    title: "Master of Science in Interaction Design",
    summary:
      "Expanding my product, interface, and interaction design perspective while continuing to connect design decisions with technical implementation.",
  },
  {
    logo: educationMalmo,
    meta: "2020 - 2023",
    school: "Malmö University",
    title: "Bachelor's degree in Media Production and Process Design",
    summary:
      "A programme focused on digital media production, web, UI/UX, process design, technical planning, databases, and interactive publishing.",
  },
  {
    logo: educationGavle,
    meta: "Focused course",
    school: "University of Gävle",
    title: "3D Modeling and Animation in an Open Source Environment",
    summary:
      "Worked with polygon modeling, materials, lighting, rigging, skeleton deformation, and keyframe animation in Blender.",
  },
  {
    logo: educationKristianstad,
    meta: "Focused course",
    school: "Kristianstad University",
    title: "Game Development for Android",
    summary:
      "Built Android game concepts while working with application structure, graphics, interaction, animation, audio, and evaluation.",
  },
  {
    logo: educationUmea,
    meta: "Focused course",
    school: "Umeå University",
    title: "Music and Sound Processing in MAX/MSP",
    summary:
      "Created and modified visual programming patches for interactive sound, music processing, and multimedia experimentation.",
  },
];

const experience = [
  {
    company: "The Cohabit",
    period: "2024",
    role: "Internship as Full-stack Developer",
    summary:
      "Developed an order management system that lets customers add products, submit orders, and receive automatic confirmation emails. Cohabit adopted and actively used the solution after the internship.",
  },
  {
    company: "The Stepstone Group",
    period: "2023/2024",
    role: "Internship as Software Engineer",
    summary:
      "Deepened my work with Google Cloud Platform, Pub/Sub, BigQuery, and OpenAI APIs while building the AI-based Ask Gandalf application.",
  },
];

type FadeInProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  id?: string;
  x?: number;
  y?: number;
};

function FadeIn({
  children,
  className,
  delay = 0,
  duration = 0.7,
  id,
  x = 0,
  y = 30,
}: FadeInProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      id={id}
      initial={prefersReducedMotion ? false : { opacity: 0, x, y }}
      transition={{
        delay,
        duration: prefersReducedMotion ? 0 : duration,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      viewport={{ once: true, margin: "50px", amount: 0 }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
    >
      {children}
    </motion.div>
  );
}

function ContactButton({ className = "" }: { className?: string }) {
  return (
    <a
      className={`hero-btn-primary ${className}`}
      href={`mailto:${contact.email}`}
    >
      <Mail aria-hidden="true" className="h-4 w-4" strokeWidth={2.2} />
      Contact Me
    </a>
  );
}

function ResumeButton({ className = "" }: { className?: string }) {
  return (
    <a
      className={`hero-btn-secondary ${className}`}
      download="Mehmet_Kaan_Taspunar_CV.docx"
      href={resumeDocx}
    >
      <Download aria-hidden="true" className="h-4 w-4" strokeWidth={2.2} />
      Resume
    </a>
  );
}

function LiveProjectButton({ href }: { href: string }) {
  return (
    <a
      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#D7E2EA] px-6 py-3 text-xs font-medium uppercase tracking-[0.18em] text-[#D7E2EA] transition-colors duration-200 hover:bg-[#D7E2EA]/10 sm:px-8 sm:py-3.5 sm:text-sm"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      Live Project
      <ArrowUpRight aria-hidden="true" className="h-4 w-4" strokeWidth={2.2} />
    </a>
  );
}

function HeroSection() {
  const navItems = [
    { href: "#about", label: "About" },
    { href: "#skills", label: "Skills" },
    { href: "#projects", label: "Projects" },
    { href: "#contact", label: "Contact" },
  ];
  const heroTags = ["React", "Node.js", "GCP", "AI Products"];

  return (
    <section
      className="hero-section relative flex h-screen min-h-[700px] flex-col overflow-hidden bg-[#0C0C0C]"
      style={{ maxHeight: "1200px" }}
    >
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-scanline" aria-hidden="true" />

      <div className="hero-content-shell relative z-10 flex h-full flex-col">
        <FadeIn delay={0} y={-20}>
          <nav
            aria-label="Primary navigation"
            className="hero-nav relative z-30 mx-auto mt-5 flex w-[calc(100%-2rem)] max-w-7xl items-center justify-between rounded-full border border-[#D7E2EA]/15 px-4 py-3 text-xs font-medium uppercase tracking-[0.18em] text-[#D7E2EA] backdrop-blur md:mt-6 md:px-6 md:text-sm"
          >
            <a className="font-black tracking-[0.28em]" href="#top">
              MKT
            </a>
            <div className="flex items-center gap-3 sm:gap-5 md:gap-8">
              {navItems.map((item) => (
                <a
                  className="transition-opacity duration-200 hover:opacity-70"
                  href={item.href}
                  key={item.label}
                >
                  {item.label}
                </a>
              ))}
            </div>
            <a
              className="hidden items-center gap-2 rounded-full border border-[#D7E2EA]/20 px-3 py-1.5 text-[0.65rem] tracking-[0.16em] text-[#D7E2EA]/80 transition-colors hover:bg-[#D7E2EA]/10 md:inline-flex"
              href={contact.linkedin}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Linkedin aria-hidden="true" className="h-3.5 w-3.5" />
              LinkedIn
            </a>
          </nav>
        </FadeIn>

        <div
          className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col px-5 pb-6 pt-6 sm:px-8 md:px-10 md:pb-9"
          style={{ paddingTop: "2.5rem" }}
        >
          <FadeIn className="hero-sidecv-frame" delay={0.28} y={32}>
            <img
              alt="Mehmet Kaan Taspunar in Venice"
              className="hero-sidecv-img"
              draggable="false"
              src={heroSideCv}
            />
          </FadeIn>

          <FadeIn className="relative z-20 md:max-w-[68%]" delay={0.15} y={40}>
            <p className="hero-kicker mb-3 text-xs font-medium uppercase tracking-[0.28em] text-[#D7E2EA]/62 md:text-sm">
              Full-stack developer / AI, cloud and product interfaces
            </p>
            <h1 className="hero-name" aria-label="Mehmet Kaan Taspunar">
              <span className="hero-name-line hero-name-line-top">Mehmet</span>
              <span className="hero-name-line hero-name-line-bottom">Kaan</span>
            </h1>
          </FadeIn>

          <div className="relative z-30 mt-auto grid max-w-xl gap-5">
            <FadeIn delay={0.35} y={22}>
              <div className="max-w-[420px]">
                <p className="text-base font-light uppercase leading-snug text-[#D7E2EA] sm:text-lg md:text-xl">
                  Building practical web, AI, and cloud products with a sharp
                  focus on usable interfaces.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {heroTags.map((tag) => (
                    <span
                      className="rounded-full border border-[#D7E2EA]/18 px-3 py-1.5 text-[0.65rem] font-medium uppercase tracking-[0.16em] text-[#D7E2EA]/72"
                      key={tag}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.5} y={22}>
              <div className="flex flex-col items-start gap-3">
                <p className="text-[0.65rem] font-medium uppercase tracking-[0.22em] text-[#D7E2EA]/55">
                  Malmö / Open to projects
                </p>
                <div className="flex flex-wrap gap-3">
                  <ResumeButton />
                  <ContactButton />
                </div>
                <div className="flex items-center gap-4 text-xs font-medium uppercase tracking-[0.18em] text-[#D7E2EA]/55">
                  <a
                    className="transition-colors hover:text-[#D7E2EA]"
                    href={contact.github}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    GitHub
                  </a>
                  <a
                    className="transition-colors hover:text-[#D7E2EA]"
                    href={contact.linkedin}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.7} y={15}>
            <div className="hero-footerline mt-5 hidden items-center justify-between text-[0.68rem] font-medium uppercase tracking-[0.22em] text-[#D7E2EA]/45 md:flex">
              <span>Selected work below</span>
              <span>Scroll</span>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function MarqueeRow({
  images,
  reverse = false,
  scrollOffset,
}: {
  images: string[];
  reverse?: boolean;
  scrollOffset: number;
}) {
  const translateX = reverse ? -(scrollOffset - 200) : scrollOffset - 200;

  return (
    <div
      className="flex gap-3"
      style={{
        transform: `translate3d(${translateX}px, 0, 0)`,
        willChange: "transform",
      }}
    >
      {Array.from({ length: 3 }).flatMap((_, repeatIndex) =>
        images.map((image, index) => (
          <img
            alt="Portfolio project screenshot"
            className="h-[210px] min-w-[330px] rounded-2xl object-cover sm:h-[270px] sm:min-w-[420px]"
            key={`${repeatIndex}-${index}-${image}`}
            loading="lazy"
            src={image}
          />
        )),
      )}
    </div>
  );
}

function MarqueeSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const rowOne = useMemo(() => marqueeImages.slice(0, 9), []);
  const rowTwo = useMemo(() => marqueeImages.slice(9), []);

  useEffect(() => {
    const updateOffset = () => {
      const section = sectionRef.current;

      if (!section) {
        return;
      }

      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const nextOffset =
        (window.scrollY - sectionTop + window.innerHeight) * 0.3;
      setScrollOffset(nextOffset);
    };

    updateOffset();
    window.addEventListener("scroll", updateOffset, { passive: true });
    window.addEventListener("resize", updateOffset);

    return () => {
      window.removeEventListener("scroll", updateOffset);
      window.removeEventListener("resize", updateOffset);
    };
  }, []);

  return (
    <section
      className="overflow-hidden bg-[#0C0C0C] pb-10 pt-24 sm:pt-32 md:pt-40"
      ref={sectionRef}
    >
      <div className="flex flex-col gap-3">
        <MarqueeRow images={rowOne} scrollOffset={scrollOffset} />
        <MarqueeRow images={rowTwo} reverse scrollOffset={scrollOffset} />
      </div>
    </section>
  );
}

function AnimatedCharacter({
  char,
  index,
  progress,
  total,
}: {
  char: string;
  index: number;
  progress: MotionValue<number>;
  total: number;
}) {
  const start = total > 1 ? (index / total) * 0.85 : 0;
  const end = Math.min(start + 0.15, 1);
  const opacity = useTransform(progress, [start, end], [0.2, 1]);
  const displayChar = char === " " ? "\u00A0" : char;

  return (
    <span className="relative inline-block" aria-hidden="true">
      <span className="invisible">{displayChar}</span>
      <motion.span className="absolute inset-0" style={{ opacity }}>
        {displayChar}
      </motion.span>
    </span>
  );
}

function AnimatedText({ text }: { text: string }) {
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const { scrollYProgress } = useScroll({
    target: paragraphRef,
    offset: ["start 0.8", "end 0.2"],
  });
  const characters = useMemo(() => Array.from(text), [text]);

  return (
    <p
      aria-label={text}
      className="max-w-[760px] text-center text-[clamp(1rem,2vw,1.35rem)] font-medium leading-relaxed text-[#D7E2EA]"
      ref={paragraphRef}
    >
      {characters.map((char, index) => (
        <AnimatedCharacter
          char={char}
          index={index}
          key={`${char}-${index}`}
          progress={scrollYProgress}
          total={characters.length}
        />
      ))}
    </p>
  );
}

function AboutSection() {
  return (
    <section
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0C0C0C] px-5 py-20 sm:px-8 md:px-10"
      id="about"
    >
      {/* <FadeIn
        className="pointer-events-none absolute left-[4%] top-[8%] z-0 w-[92px] opacity-40 sm:w-[140px] md:w-[180px]"
        delay={0.1}
        duration={0.9}
        x={-80}
        y={0}
      >
        <img alt="" className="block h-auto w-full" src={skillCloud} />
      </FadeIn>
      <FadeIn
        className="pointer-events-none absolute bottom-[8%] right-[5%] z-0 w-[96px] opacity-40 sm:w-[140px] md:w-[180px]"
        delay={0.2}
        duration={0.9}
        x={80}
        y={0}
      >
        <img alt="" className="block h-auto w-full" src={skillFrontend} />
      </FadeIn> */}

      <div className="relative z-10 flex max-w-5xl flex-col items-center gap-10 sm:gap-14 md:gap-16">
        <FadeIn delay={0} y={40}>
          <h2 className="hero-heading text-center text-[clamp(3rem,12vw,160px)] font-black uppercase leading-none tracking-tight">
            About me
          </h2>
        </FadeIn>

        <div className="flex flex-col items-center gap-12 sm:gap-16 md:gap-20">
          <AnimatedText text="My name is Mehmet, and I graduated in 2023 with a Bachelor's degree in Media Production and Process Design. I am now pursuing a Master's in Interaction Design at Malmö University. I build full-stack web, AI, and cloud-based products, combining frontend craft, backend logic, and practical design thinking." />

          <div className="grid w-full gap-4 md:grid-cols-2">
            {experience.map((item, index) => (
              <FadeIn delay={0.1 * index} key={item.company} y={20}>
                <article className="h-full rounded-[28px] border border-[#D7E2EA]/25 bg-[#D7E2EA]/5 p-5 text-left backdrop-blur sm:p-6">
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <Briefcase
                      aria-hidden="true"
                      className="mt-1 h-5 w-5 shrink-0 text-[#D7E2EA]"
                    />
                    <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#D7E2EA]/65">
                      {item.period}
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase leading-none text-[#D7E2EA]">
                    {item.company}
                  </h3>
                  <p className="mt-2 text-sm font-medium uppercase tracking-[0.14em] text-[#D7E2EA]/70">
                    {item.role}
                  </p>
                  <p className="mt-5 text-base font-light leading-relaxed text-[#D7E2EA]/75">
                    {item.summary}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <ResumeButton />
            <ContactButton />
          </div>
        </div>
      </div>
    </section>
  );
}

function SkillsSection() {
  return (
    <section
      className="rounded-t-[40px] bg-white px-5 py-20 text-[#0C0C0C] sm:rounded-t-[50px] sm:px-8 sm:py-24 md:rounded-t-[60px] md:px-10 md:py-32"
      id="skills"
    >
      <h2 className="mb-16 text-center text-[clamp(3rem,12vw,160px)] font-black uppercase leading-none tracking-tight sm:mb-20 md:mb-28">
        Skills
      </h2>

      <div className="mx-auto max-w-5xl">
        {skills.map((skill, index) => (
          <FadeIn delay={index * 0.1} key={skill.name} y={30}>
            <article className="grid grid-cols-[5.5rem_1fr] gap-5 border-t border-[rgba(12,12,12,0.15)] py-8 last:border-b sm:grid-cols-[8rem_1fr] sm:gap-8 sm:py-10 md:grid-cols-[11rem_1fr] md:py-12">
              <span className="text-[clamp(3rem,10vw,140px)] font-black leading-none text-[#0C0C0C]">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="flex min-w-0 flex-col justify-center gap-3">
                <h3 className="text-[clamp(1rem,2.2vw,2.1rem)] font-medium uppercase leading-tight">
                  {skill.name}
                </h3>
                <p className="max-w-2xl text-[clamp(0.85rem,1.6vw,1.25rem)] font-light leading-relaxed opacity-60">
                  {skill.description}
                </p>
              </div>
            </article>
          </FadeIn>
        ))}
      </div>

      <div className="mx-auto mt-16 grid max-w-6xl gap-5 lg:grid-cols-[0.8fr_1.2fr]">
        <FadeIn y={30}>
          <div className="h-full overflow-hidden rounded-[32px] border border-[rgba(12,12,12,0.12)] bg-[#0C0C0C]">
            <img
              alt="Resume preview"
              className="h-full min-h-[440px] w-full object-cover object-top opacity-95"
              loading="lazy"
              src={resumePreview}
            />
          </div>
        </FadeIn>

        <FadeIn delay={0.1} y={30}>
          <div className="flex h-full flex-col justify-between rounded-[32px] border border-[rgba(12,12,12,0.12)] bg-[#F2F4F6] p-6 sm:p-8">
            <div>
              <p className="mb-4 text-sm font-medium uppercase tracking-[0.2em] opacity-55">
                CV snapshot
              </p>
              <h3 className="text-[clamp(2rem,5vw,4.5rem)] font-black uppercase leading-none tracking-tight">
                Mehmet Kaan Taspunar
              </h3>
              <p className="mt-4 text-xl font-medium uppercase tracking-[0.14em] opacity-70">
                Full-stack Developer
              </p>
              <p className="mt-8 max-w-2xl text-[clamp(1rem,1.8vw,1.2rem)] font-light leading-relaxed opacity-70">
                Highly motivated and detail-oriented developer with a passion
                for efficient, innovative, and user-friendly software solutions.
                My work connects frontend, backend, AI, cloud, and product
                design into practical applications.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0C0C0C] px-7 py-3 text-sm font-medium uppercase tracking-[0.16em] text-white transition-opacity hover:opacity-80"
                download="Mehmet_Kaan_Taspunar_CV.docx"
                href={resumeDocx}
              >
                <Download aria-hidden="true" className="h-4 w-4" />
                Download CV
              </a>
              <a
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0C0C0C]/30 px-7 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-colors hover:bg-[#0C0C0C]/5"
                href={contact.linkedin}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Linkedin aria-hidden="true" className="h-4 w-4" />
                LinkedIn
              </a>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function EducationSection() {
  return (
    <section className="bg-white px-5 pb-28 text-[#0C0C0C] sm:px-8 md:px-10">
      <div className="mx-auto max-w-6xl border-t border-[rgba(12,12,12,0.15)] pt-16">
        <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <h2 className="text-[clamp(2.8rem,9vw,120px)] font-black uppercase leading-none tracking-tight">
            Education
          </h2>
          <p className="max-w-xl text-[clamp(0.95rem,1.4vw,1.15rem)] font-light leading-relaxed opacity-65">
            Formal education across interaction design, media production, 3D,
            Android games, and sound processing.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {education.map((item, index) => (
            <FadeIn delay={index * 0.06} key={`${item.school}-${item.title}`}>
              <article className="flex h-full gap-4 rounded-[28px] border border-[rgba(12,12,12,0.12)] bg-[#F2F4F6] p-5 sm:p-6">
                <img
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-2xl object-cover"
                  loading="lazy"
                  src={item.logo}
                />
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.18em] opacity-50">
                    {item.meta}
                  </p>
                  <h3 className="mt-2 text-2xl font-black uppercase leading-none">
                    {item.school}
                  </h3>
                  <p className="mt-2 text-base font-medium uppercase leading-snug tracking-[0.08em] opacity-70">
                    {item.title}
                  </p>
                  <p className="mt-4 text-base font-light leading-relaxed opacity-65">
                    {item.summary}
                  </p>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ index, project }: { index: number; project: Project }) {
  const cardStyle = {
    "--card-offset": `${index * 18}px`,
    zIndex: index + 1,
  } as MotionStyle;

  return (
    <motion.article
      className="project-card sticky rounded-[40px] border-2 border-[#D7E2EA] bg-[#0C0C0C] p-4 sm:rounded-[50px] sm:p-6 md:rounded-[60px] md:p-7"
      style={cardStyle}
    >
      <div className="grid gap-4 pb-4 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:pb-5">
        <span className="text-[clamp(3.2rem,9vw,110px)] font-black leading-none text-[#D7E2EA]">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#D7E2EA]/65 sm:text-sm">
            {project.category}
          </p>
          <h3 className="mt-2 text-[clamp(1.5rem,3.4vw,3.9rem)] font-black uppercase leading-none tracking-tight text-[#D7E2EA]">
            {project.name}
          </h3>
          <p className="project-card-description mt-3 max-w-3xl text-sm font-light leading-relaxed text-[#D7E2EA]/70">
            {project.description}
          </p>
        </div>
        <LiveProjectButton href={project.url} />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {project.stack.slice(0, 4).map((item) => (
          <span
            className="rounded-full border border-[#D7E2EA]/25 px-3 py-1 text-[0.65rem] font-medium uppercase tracking-[0.14em] text-[#D7E2EA]/75"
            key={item}
          >
            {item}
          </span>
        ))}
      </div>

      <div className="project-card-images grid min-h-0 flex-1 gap-3 lg:grid-cols-[40%_1fr]">
        <div className="grid min-h-0 gap-3 lg:grid-rows-[0.95fr_1.05fr]">
          <img
            alt={`${project.name} preview detail`}
            className="project-card-image"
            loading="lazy"
            src={project.images[0]}
          />
          <img
            alt={`${project.name} secondary preview`}
            className="project-card-image"
            loading="lazy"
            src={project.images[1]}
          />
        </div>

        <img
          alt={`${project.name} main preview`}
          className="project-card-image"
          loading="lazy"
          src={project.images[2]}
        />
      </div>
    </motion.article>
  );
}

function ContactSection() {
  const links = [
    {
      href: `mailto:${contact.email}`,
      icon: Mail,
      label: "Email",
      text: contact.email,
    },
    {
      href: contact.phoneHref,
      icon: Phone,
      label: "Phone",
      text: contact.phoneDisplay,
    },
    {
      href: contact.linkedin,
      icon: Linkedin,
      label: "LinkedIn",
      text: "Mehmet Kaan Taspunar",
    },
    {
      href: contact.github,
      icon: Github,
      label: "GitHub",
      text: "Mehmet-Kaan",
    },
  ];

  return (
    <FadeIn
      className="mx-auto mt-12 max-w-6xl border-t border-[#D7E2EA]/20 pt-12"
      id="contact"
      y={30}
    >
      <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
        <div>
          <p className="mb-4 flex items-center gap-2 text-sm font-medium uppercase tracking-[0.2em] text-[#D7E2EA]/65">
            <MapPin aria-hidden="true" className="h-4 w-4" />
            {contact.location}
          </p>
          <h2 className="hero-heading text-[clamp(3rem,9vw,120px)] font-black uppercase leading-none tracking-tight">
            Contact
          </h2>
          <p className="mt-6 max-w-xl text-[clamp(1rem,1.8vw,1.25rem)] font-light leading-relaxed text-[#D7E2EA]/75">
            I am open to full-stack roles, freelance projects, and
            collaborations where practical software, thoughtful UI, and reliable
            delivery matter.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {links.map((item) => {
            const Icon = item.icon;

            return (
              <a
                className="group rounded-[28px] border border-[#D7E2EA]/20 p-5 transition-colors hover:bg-[#D7E2EA]/10"
                href={item.href}
                key={item.label}
                rel={
                  item.href.startsWith("http")
                    ? "noopener noreferrer"
                    : undefined
                }
                target={item.href.startsWith("http") ? "_blank" : undefined}
              >
                <div className="mb-5 flex items-center justify-between gap-4">
                  <Icon
                    aria-hidden="true"
                    className="h-5 w-5 text-[#D7E2EA]"
                    strokeWidth={2.1}
                  />
                  <ArrowUpRight
                    aria-hidden="true"
                    className="h-4 w-4 text-[#D7E2EA]/45 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.1}
                  />
                </div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#D7E2EA]/50">
                  {item.label}
                </p>
                <p className="mt-2 break-words text-base font-medium text-[#D7E2EA]">
                  {item.text}
                </p>
              </a>
            );
          })}
        </div>
      </div>
    </FadeIn>
  );
}

function ProjectsSection() {
  const { projects } = useProjects();

  return (
    <section
      className="relative z-10 -mt-10 rounded-t-[40px] bg-[#0C0C0C] px-5 pb-24 pt-20 text-[#D7E2EA] sm:-mt-12 sm:rounded-t-[50px] sm:px-8 sm:pt-24 md:-mt-14 md:rounded-t-[60px] md:px-10 md:pt-32"
      id="projects"
    >
      <FadeIn y={40}>
        <div className="mx-auto mb-12 max-w-6xl text-center sm:mb-16 md:mb-20">
          <p className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-[#D7E2EA]/55">
            Featured work
          </p>
          <h2 className="hero-heading text-[clamp(3rem,12vw,160px)] font-black uppercase leading-none tracking-tight">
            Projects
          </h2>
        </div>
      </FadeIn>

      <div className="project-stack mx-auto max-w-7xl">
        {projects.map((project, index) => (
          <ProjectCard index={index} key={project.name} project={project} />
        ))}
      </div>

      <ContactSection />
    </section>
  );
}

function CreatorPortfolio() {
  return (
    <main className="creator-portfolio min-h-screen overflow-x-clip bg-[#0C0C0C] font-kanit">
      <HeroSection />
      <MarqueeSection />
      <AboutSection />
      <SkillsSection />
      <EducationSection />
      <ProjectsSection />
    </main>
  );
}

export default CreatorPortfolio;
